<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Plus, Pencil, Trash2, X as XIcon } from 'lucide-vue-next';
import { useAuth } from '../composables/useAuth';
import { useServices } from '../composables/useServices';
import type { Service } from '../services/api';

const router = useRouter();
const { t } = useI18n();
const { user, fetchUser } = useAuth();
const {
  services,
  isLoading,
  error,
  currentPage,
  hasMore,
  fetchServices,
  createService,
  updateService,
  deleteService,
} = useServices();

// Modal states
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const selectedService = ref<Service | null>(null);

// The form's own shape: every numeric field is empty until typed, while the API
// requires a duration. Validation below is what bridges the two.
interface ServiceFormData {
  name: string;
  price_from: number | null;
  price_to: number | null;
  time: number | null;
}

// Form states
const formData = ref<ServiceFormData>({
  name: '',
  price_from: null,
  price_to: null,
  time: null,
});

const formErrors = ref<Record<string, string>>({});

const validateForm = (): boolean => {
  formErrors.value = {};

  if (!formData.value.name || formData.value.name.trim() === '') {
    formErrors.value.name = t('services.error_name_required');
  }

  if (
    formData.value.price_from !== null &&
    formData.value.price_from !== undefined &&
    formData.value.price_from < 0
  ) {
    formErrors.value.price_from = t('services.error_price_from_negative');
  }

  if (
    formData.value.price_to !== null &&
    formData.value.price_to !== undefined &&
    formData.value.price_to < 0
  ) {
    formErrors.value.price_to = t('services.error_price_to_negative');
  }

  if (
    formData.value.price_from !== null &&
    formData.value.price_from !== undefined &&
    formData.value.price_to !== null &&
    formData.value.price_to !== undefined &&
    formData.value.price_to < formData.value.price_from
  ) {
    formErrors.value.price_to = t('services.error_price_range');
  }

  // A missing duration used to pass validation and reach an endpoint that requires
  // one, so an empty field is an error in its own right.
  if (formData.value.time === null || formData.value.time <= 0) {
    formErrors.value.time = t('services.error_duration_positive');
  }

  return Object.keys(formErrors.value).length === 0;
};

const resetForm = () => {
  formData.value = {
    name: '',
    price_from: null,
    price_to: null,
    time: null,
  };
  formErrors.value = {};
};

const openCreateModal = () => {
  resetForm();
  isCreateModalOpen.value = true;
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  resetForm();
};

const openEditModal = (service: Service) => {
  selectedService.value = service;
  formData.value = {
    name: service.name,
    price_from: service.price_from,
    price_to: service.price_to,
    time: service.time,
  };
  formErrors.value = {};
  isEditModalOpen.value = true;
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  selectedService.value = null;
  resetForm();
};

const openDeleteModal = (service: Service) => {
  selectedService.value = service;
  isDeleteModalOpen.value = true;
};

const closeDeleteModal = () => {
  isDeleteModalOpen.value = false;
  selectedService.value = null;
};

const handleCreate = async () => {
  if (!validateForm()) return;

  // validateForm has already rejected a null duration; this narrows it for the API
  // type rather than asserting it away.
  const { time } = formData.value;
  if (time === null) return;

  const success = await createService({ ...formData.value, time });
  if (success) {
    closeCreateModal();
  }
};

const handleUpdate = async () => {
  if (!validateForm() || !selectedService.value) return;

  const { time } = formData.value;
  if (time === null) return;

  const success = await updateService(selectedService.value.id, { ...formData.value, time });
  if (success) {
    closeEditModal();
  }
};

const handleDelete = async () => {
  if (!selectedService.value) return;

  const success = await deleteService(selectedService.value.id);
  if (success) {
    closeDeleteModal();
  }
};

const handleLoadMore = () => {
  if (hasMore.value && !isLoading.value) {
    fetchServices(currentPage.value + 1);
  }
};

const formatPriceRange = (from: number | null, to: number | null): string => {
  if (from === null && to === null) return t('services.no_price');
  if (from === to) return `${from?.toFixed(2)} zł`;
  return `${from?.toFixed(2)} – ${to?.toFixed(2)} zł`;
};

const formatTime = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }
  return `${minutes} min`;
};

onMounted(async () => {
  if (!user.value) {
    const userData = await fetchUser();
    if (!userData) {
      router.push('/login');
      return;
    }
  }

  fetchServices(1);
});
</script>

<template>
  <div class="flex flex-col min-h-screen bg-[#0A0A0B]">
    <!-- Main Content -->
    <div class="flex-1 p-4 md:p-6 lg:p-8">
      <div class="max-w-7xl mx-auto">
        <!-- Header with Add Button -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-bold text-[#F5F0EA]">
              {{ $t('services.title') }}
            </h2>
            <p class="text-[#524840] mt-1">{{ $t('services.subtitle') }}</p>
          </div>
          <button
            @click="openCreateModal"
            class="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#D4956A] hover:bg-[#E8B48A] text-[#0A0A0B] rounded-xl transition font-medium"
          >
            <Plus class="w-4 h-4" />
            {{ $t('services.add_button') }}
          </button>
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-6 p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400"
        >
          {{ error }}
        </div>

        <!-- Services Table -->
        <div class="bg-[#111113] rounded-xl border border-[#2A2018] overflow-hidden">
          <!-- Desktop Table -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full">
              <thead class="bg-[#1E1E23] border-b border-[#2A2018]">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ $t('services.table_name') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ $t('services.table_price') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ $t('services.table_duration') }}
                  </th>
                  <th
                    class="px-6 py-3 text-right text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ $t('services.table_actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-[#2A2018]">
                <tr v-if="isLoading && currentPage === 1" class="animate-pulse">
                  <td colspan="4" class="px-6 py-12 text-center text-[#524840]">
                    {{ $t('services.loading') }}
                  </td>
                </tr>
                <tr v-else-if="services.length === 0" class="hover:bg-[#1E1E23]">
                  <td colspan="4" class="px-6 py-12 text-center text-[#524840]">
                    {{ $t('services.empty') }}
                  </td>
                </tr>
                <tr
                  v-for="service in services"
                  :key="service.id"
                  class="hover:bg-[#1E1E23] transition"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-[#F5F0EA]">
                      {{ service.name }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-[#F5F0EA]">
                      {{ formatPriceRange(service.price_from, service.price_to) }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-[#F5F0EA]">
                      {{ formatTime(service.time) }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="flex items-center justify-end gap-2">
                      <button
                        @click="openEditModal(service)"
                        class="p-2 text-[#524840] hover:text-blue-600 hover:bg-blue-900/20 rounded-lg transition"
                        :title="$t('services.edit_tooltip')"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                      <button
                        @click="openDeleteModal(service)"
                        class="p-2 text-[#524840] hover:text-red-600 hover:bg-red-900/20 rounded-lg transition"
                        :title="$t('services.delete_tooltip')"
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Cards -->
          <div class="md:hidden divide-y divide-[#2A2018]">
            <div v-if="isLoading && currentPage === 1" class="p-6 text-center text-[#524840]">
              {{ $t('services.loading') }}
            </div>
            <div v-else-if="services.length === 0" class="p-6 text-center text-[#524840]">
              {{ $t('services.empty') }}
            </div>
            <div
              v-for="service in services"
              :key="service.id"
              class="p-4 hover:bg-[#1E1E23] transition"
            >
              <div class="flex items-start justify-between gap-4">
                <div class="flex-1 min-w-0">
                  <h3 class="text-base font-medium text-[#F5F0EA] mb-2">
                    {{ service.name }}
                  </h3>
                  <div class="flex items-center gap-4 text-sm text-[#524840]">
                    <span>{{ formatPriceRange(service.price_from, service.price_to) }}</span>
                    <span>{{ formatTime(service.time) }}</span>
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  <button
                    @click="openEditModal(service)"
                    class="p-2 text-[#524840] hover:text-blue-600 hover:bg-blue-900/20 rounded-lg transition"
                  >
                    <Pencil class="w-4 h-4" />
                  </button>
                  <button
                    @click="openDeleteModal(service)"
                    class="p-2 text-[#524840] hover:text-red-600 hover:bg-red-900/20 rounded-lg transition"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="hasMore && services.length > 0" class="border-t border-[#2A2018] p-4">
            <button
              @click="handleLoadMore"
              :disabled="isLoading"
              class="w-full py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] disabled:bg-[#111113] disabled:cursor-not-allowed text-[#9E9080] rounded-xl transition font-medium"
            >
              {{ isLoading ? $t('services.loading_more') : $t('services.load_more') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <div v-if="isCreateModalOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        @click="closeCreateModal"
      />
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative bg-[#111113] rounded-2xl shadow-xl max-w-md w-full p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-semibold text-[#F5F0EA]">
              {{ $t('services.create_modal_title') }}
            </h3>
            <button
              @click="closeCreateModal"
              class="p-2 text-[#524840] hover:text-[#F5F0EA] hover:bg-[#1E1E23] rounded-lg transition"
            >
              <XIcon class="w-5 h-5" />
            </button>
          </div>

          <form @submit.prevent="handleCreate" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('services.form_name_label') }}
              </label>
              <input
                v-model="formData.name"
                type="text"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                :placeholder="$t('services.form_name_placeholder')"
                :class="{ 'border-red-500': formErrors.name }"
              />
              <p v-if="formErrors.name" class="mt-1 text-sm text-red-400">
                {{ formErrors.name }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-[#9E9080] mb-1">
                  {{ $t('services.form_price_from_label') }}
                </label>
                <input
                  v-model.number="formData.price_from"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                  placeholder="0.00"
                  :class="{ 'border-red-500': formErrors.price_from }"
                />
                <p v-if="formErrors.price_from" class="mt-1 text-sm text-red-400">
                  {{ formErrors.price_from }}
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-[#9E9080] mb-1">
                  {{ $t('services.form_price_to_label') }}
                </label>
                <input
                  v-model.number="formData.price_to"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                  placeholder="0.00"
                  :class="{ 'border-red-500': formErrors.price_to }"
                />
                <p v-if="formErrors.price_to" class="mt-1 text-sm text-red-400">
                  {{ formErrors.price_to }}
                </p>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('services.form_duration_label') }}
              </label>
              <input
                v-model.number="formData.time"
                type="number"
                min="1"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                :placeholder="$t('services.form_duration_placeholder')"
                :class="{ 'border-red-500': formErrors.time }"
              />
              <p v-if="formErrors.time" class="mt-1 text-sm text-red-400">
                {{ formErrors.time }}
              </p>
            </div>

            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeCreateModal"
                class="flex-1 py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] rounded-xl transition font-medium"
              >
                {{ $t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="isLoading"
                class="flex-1 py-2.5 px-4 bg-[#D4956A] hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] text-[#0A0A0B] rounded-xl transition font-medium"
              >
                {{ isLoading ? $t('services.form_adding') : $t('services.form_add_submit') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="isEditModalOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        @click="closeEditModal"
      />
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative bg-[#111113] rounded-2xl shadow-xl max-w-md w-full p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-semibold text-[#F5F0EA]">
              {{ $t('services.edit_modal_title') }}
            </h3>
            <button
              @click="closeEditModal"
              class="p-2 text-[#524840] hover:text-[#F5F0EA] hover:bg-[#1E1E23] rounded-lg transition"
            >
              <XIcon class="w-5 h-5" />
            </button>
          </div>

          <form @submit.prevent="handleUpdate" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('services.form_name_label') }}
              </label>
              <input
                v-model="formData.name"
                type="text"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                :placeholder="$t('services.form_name_placeholder')"
                :class="{ 'border-red-500': formErrors.name }"
              />
              <p v-if="formErrors.name" class="mt-1 text-sm text-red-400">
                {{ formErrors.name }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-[#9E9080] mb-1">
                  {{ $t('services.form_price_from_label') }}
                </label>
                <input
                  v-model.number="formData.price_from"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                  placeholder="0.00"
                  :class="{ 'border-red-500': formErrors.price_from }"
                />
                <p v-if="formErrors.price_from" class="mt-1 text-sm text-red-400">
                  {{ formErrors.price_from }}
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-[#9E9080] mb-1">
                  {{ $t('services.form_price_to_label') }}
                </label>
                <input
                  v-model.number="formData.price_to"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                  placeholder="0.00"
                  :class="{ 'border-red-500': formErrors.price_to }"
                />
                <p v-if="formErrors.price_to" class="mt-1 text-sm text-red-400">
                  {{ formErrors.price_to }}
                </p>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-[#9E9080] mb-1">
                {{ $t('services.form_duration_label') }}
              </label>
              <input
                v-model.number="formData.time"
                type="number"
                min="1"
                class="w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
                :placeholder="$t('services.form_duration_placeholder')"
                :class="{ 'border-red-500': formErrors.time }"
              />
              <p v-if="formErrors.time" class="mt-1 text-sm text-red-400">
                {{ formErrors.time }}
              </p>
            </div>

            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeEditModal"
                class="flex-1 py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] rounded-xl transition font-medium"
              >
                {{ $t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="isLoading"
                class="flex-1 py-2.5 px-4 bg-[#D4956A] hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] text-[#0A0A0B] rounded-xl transition font-medium"
              >
                {{ isLoading ? $t('services.form_saving') : $t('services.form_edit_submit') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="isDeleteModalOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        @click="closeDeleteModal"
      />
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative bg-[#111113] rounded-2xl shadow-xl max-w-sm w-full p-6">
          <div
            class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-900/30 rounded-full"
          >
            <Trash2 class="w-6 h-6 text-red-400" />
          </div>

          <h3 class="text-lg font-semibold text-[#F5F0EA] text-center mb-2">
            {{ $t('services.delete_modal_title') }}
          </h3>
          <p class="text-[#524840] text-center mb-6">
            {{ $t('services.delete_confirm', { name: selectedService?.name }) }}
          </p>

          <div class="flex gap-3">
            <button
              type="button"
              @click="closeDeleteModal"
              class="flex-1 py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] rounded-xl transition font-medium"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              @click="handleDelete"
              :disabled="isLoading"
              class="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-[#F5F0EA] rounded-xl transition font-medium"
            >
              {{ isLoading ? $t('services.form_deleting') : $t('services.form_delete_confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
